import WhatsAppIcon from "@/components/WhatsAppIcon";
import { BUSINESS, businessSchema } from "@/lib/business";
import { Link, useSearchParams } from "react-router-dom";
import { Phone, Star } from "lucide-react";
import { SEO } from "@/components/SEO";
import { EnquiryForm } from "@/components/EnquiryForm";
import { useSettings } from "@/components/Layout";
import { waLink } from "@/lib/api";
import { isIndiaRegion } from "@/lib/serviceAreas";

export default function Contact() {
  const settings = useSettings();
  const [params] = useSearchParams();
  const service = params.get("service") || "";
  const state = isIndiaRegion(params.get("state")) ? params.get("state") : "";
  const whatsappMessage = (service ? `Hello Mynavodayans, I would like to enquire about ${service.replace(/-/g, " ")}.` : "Hello Mynavodayans, I would like to enquire about your services.") + (state ? ` My business location is in ${state}.` : "");
  const coverageParams = new URLSearchParams();
  if (service) coverageParams.set("service", service);
  if (state) coverageParams.set("state", state);
  const coverageQuery = coverageParams.toString();

  return (
    <div className="container-x py-14" data-testid="contact-page">
      <SEO title="Contact Mynavodayans | Business Services Across India"
           description="Enquire from anywhere in India about registration, licensing, compliance, websites and apps. Contact our Satna office: call/WhatsApp +91 9424706212."
           canonicalPath="/contact" jsonLd={businessSchema(settings)} pageType="ContactPage" />
      <nav aria-label="Breadcrumb" className="text-xs text-slate-500" data-testid="breadcrumbs">
        <a href="/" className="hover:text-[#b07f0a]">Home</a> <span aria-hidden="true">/</span> <span className="font-semibold text-slate-700">Contact</span>
      </nav>
      <div className="mt-4 grid gap-12 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-[#0a192f] sm:text-4xl" data-testid="contact-headline">Request a Quote</h1>
          <p className="mt-4 leading-relaxed text-slate-600">
            Tell us your requirement, city and state. Our Satna-based team supports business enquiries across India and will explain the applicable steps, availability and quotation. Prefer to talk right away? Reach us directly:
          </p>
          <p className="mt-3 text-sm text-slate-600">
            <Link to={"/service-areas" + (coverageQuery ? "?" + coverageQuery : "")} className="font-semibold text-[#b07f0a] underline underline-offset-4" data-testid="contact-service-coverage-link">Service Areas — India</Link>
          </p>
          <div className="mt-8 space-y-4">
            <a href={settings.phone_link}
               className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#d4a017]" data-testid="contact-call-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0a192f] text-[#f5c542]"><Phone size={20} /></span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">Call us</span>
                <span className="block font-heading text-lg font-extrabold text-[#0a192f]">{settings.phone_display}</span>
              </span>
            </a>
            <a href={waLink(settings.whatsapp_number, whatsappMessage)}
               target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#25d366]" data-testid="contact-whatsapp-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#25d366] text-white"><WhatsAppIcon size={20} /></span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">WhatsApp</span>
                <span className="block font-heading text-lg font-extrabold text-[#0a192f]">Chat with us instantly</span>
              </span>
            </a>
            <a href={settings.google_review_link} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-[#d4a017]" data-testid="contact-review-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#d4a017] text-[#0a192f]"><Star size={20} /></span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">Happy with our work?</span>
                <span className="block font-heading text-lg font-extrabold text-[#0a192f]">Review Us on Google</span>
              </span>
            </a>
          </div>
          <section className="mt-7 rounded-2xl border border-slate-200 bg-white p-5" data-testid="contact-office">
            <h2 className="font-heading text-xl font-bold text-[#0a192f]">Satna office</h2>
            <address className="mt-3 not-italic leading-relaxed text-slate-600">{settings.address || BUSINESS.address}</address>
            <a href={BUSINESS.maps} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block font-semibold text-[#b07f0a] underline" data-testid="contact-maps-link">Find us on Google Maps</a>
          </section>
          <p className="mt-6 rounded-xl bg-slate-100 px-5 py-4 text-xs leading-relaxed text-slate-500">
            We respond during business hours, usually the same day. Your details are used only to respond to your enquiry — see our <a href="/privacy-policy" className="font-semibold underline">Privacy Policy</a>.
          </p>
        </div>
        <div className="h-fit rounded-2xl border border-slate-200 bg-white p-6 shadow-lg lg:p-8">
          <EnquiryForm prefillService={service} prefillState={state} sourcePage="/contact" />
        </div>
      </div>
    </div>
  );
}
